import {groupBy} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {RefObject, useMemo, useRef, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {FixedPlateModalStep} from 'constants/analytics-products/SamplesAnalyticsConstants';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {PrimaryExperimentalRoleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import AnnotatePlateSettings from 'samples/plate-maps/annotate-plate-modal/AnnotatePlateSettings';
import {ConcentrationSettingsProps} from 'samples/plate-maps/annotate-plate-modal/ConcentrationLayerSettings';
import {PlateMapDisplayAttributeState} from 'samples/plate-maps/hooks/usePlateMapDisplayAttributeState';
import {
  AssignmentGroup,
  AssignmentGroupValue,
  CONCENTRATION_ATTRIBUTE_SENTINEL,
  PlateMap,
  PlateMapWell,
  PlateMapWellDisplayLayer,
  PlateModalAnalyticsMetadata,
  SchemaFieldAssignmentGroup,
  SchemaFieldDisplayLayer,
  SchematizedAttribute,
} from 'samples/plate-maps/PlateMapConstants';
import PlateMapPane from 'samples/plate-maps/PlateMapPane';
import {
  applyEditsToAllWells,
  applyEditToSelected,
  applySchemaTagEditToAllWells,
  applySchemaTagEditToSelected,
  getDisplayLayerAttributeKey,
  getInitialMinimalTag,
} from 'samples/plate-maps/PlateMapUtil';
import {PlateMapWellSelectionSection} from 'samples/plate-maps/PlateMapWellSelectionSection';
import PlateMapValidationBanners from 'samples/plate-maps/validation/PlateMapValidationBanners';
import {
  clearSchemaFieldErrors,
  getErrorKeyForDisplayLayerAttribute,
} from 'samples/plate-maps/validation/PlateMapValidationUtil';
import {
  FieldValuePlateMapError,
  LayerPlateMapError,
  PlateMapAnnotationError,
  PlateMapErrorType,
  ValidationErrorsByLayerAndLevel,
  WellPlateMapError,
} from 'samples/plate-maps/validation/ValidationTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import Badge, {BadgeColor} from 'ui-library/Badge';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';

export interface AnnotatePlateStepProps {
  plateMap: PlateMap;
  setPlateMap: (plateMap: PlateMap) => void;
  initialSelectedWellIndices: Set<number>;
  maxPlateViewHeight: string;
  modalRef?: RefObject<HTMLDivElement>;
  analyticsMetadata: PlateModalAnalyticsMetadata;
  displayState: PlateMapDisplayAttributeState;
  showFinalMeasurements: boolean;
  isViewOnly?: boolean;
  groupedValidationErrors?: ValidationErrorsByLayerAndLevel;
  onRemoveLayer: (selectedAttribute: SchematizedAttribute) => void;
  onDismissErrors: (errorUuids: Set<string>) => void;
  /** Type of the plate map; used to render slightly different errors/warnings for designs. */
  plateMapType: PlateMapType;
  concentrationSettingsProps?: ConcentrationSettingsProps;
}

/**
 * Content of modal step that applies role and schema field value annotations to a plate.
 */
export default function AnnotatePlateStep({
  plateMap,
  setPlateMap,
  initialSelectedWellIndices,
  modalRef,
  analyticsMetadata,
  maxPlateViewHeight,
  displayState,
  showFinalMeasurements,
  isViewOnly,
  groupedValidationErrors,
  onRemoveLayer,
  onDismissErrors,
  plateMapType,
  concentrationSettingsProps,
}: AnnotatePlateStepProps): JSX.Element {
  const previewSectionRef = useRef<HTMLDivElement>(null);
  const previewHeaderRef = useRef<HTMLDivElement>(null);
  const previewHeaderTextRef = useRef<HTMLParagraphElement>(null);

  const [selectedWellIndices, setSelectedWellIndices] = useState<Set<number>>(initialSelectedWellIndices);
  const [plateMapWithPreview, setPlateMapWithPreview] = useState<PlateMap | null>(null);
  const {assignmentGroupsPerAttribute, setAssignmentGroupsPerAttribute, selectedAttribute, displayLayer} =
    displayState;

  const [isAIPreview, setIsAIPreview] = useState<boolean>(false);

  function deselectWells(): void {
    setSelectedWellIndices(new Set());
  }

  function trackEventWithStep(event: analytics.AnalyticsEvent): void {
    analytics.trackEvent(event, {
      ...analyticsMetadata,
      step: FixedPlateModalStep.ANNOTATE_PLATE_STEP,
    });
  }

  function handleClickClear(): void {
    trackEventWithStep(STORABLE_ANALYTICS_ACTIONS.CLICKED_CLEAR_CELLS);

    let updatedPlateMap: PlateMap;
    if (displayLayer === PlateMapWellDisplayLayer.REAGENT) {
      // This shouldn't happen, but short-circuit if it does.
      return;
    } else if (displayLayer === PlateMapWellDisplayLayer.WELL_ROLE) {
      const newValue: Partial<PlateMapWell> = {role: null};
      if (selectedWellIndices.size === 0) {
        updatedPlateMap = applyEditsToAllWells(plateMap, newValue);
      } else {
        updatedPlateMap = applyEditToSelected(plateMap, newValue, selectedWellIndices);
      }
    } else {
      const schematizedAttribute = selectedAttribute as SchematizedAttribute;
      const newValue = getInitialMinimalTag(schematizedAttribute.schemaField);
      if (selectedWellIndices.size === 0) {
        updatedPlateMap = applySchemaTagEditToAllWells(
          plateMap,
          selectedAttribute as SchematizedAttribute,
          newValue
        );
      } else {
        updatedPlateMap = applySchemaTagEditToSelected(
          plateMap,
          selectedAttribute as SchematizedAttribute,
          newValue,
          selectedWellIndices
        );
      }
    }
    setPlateMap(updatedPlateMap);
    deselectWells();
  }

  function setAssignmentGroupsForSelectedAttribute(newGroupsById: {
    [id: string]: AssignmentGroup<AssignmentGroupValue>;
  }): void {
    setAssignmentGroupsPerAttribute({
      ...assignmentGroupsPerAttribute,
      [getDisplayLayerAttributeKey(selectedAttribute)]: newGroupsById,
    });
  }

  function onChangeWellSelection(indices: Set<number>): void {
    trackEventWithStep(STORABLE_ANALYTICS_ACTIONS.CHANGED_CELL_SELECTION);
    setSelectedWellIndices(indices);
  }

  function onEditPlateMap(
    newPlateMap: PlateMap,
    roleTrackingParams: {
      primaryRole?: PrimaryExperimentalRoleType;
      subrole?: string | null;
      annotatedByAi?: boolean;
    }
  ): void {
    analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.EDITED_ANNOTATIONS, {
      ...analyticsMetadata,
      ...roleTrackingParams,
    });
    setPlateMap(newPlateMap);
  }

  function onResolveErrors(errors: Array<PlateMapAnnotationError>): void {
    assert(typeof selectedAttribute !== 'string', 'Unexpected annotation errors for non-schema field layer');
    assert(
      selectedAttribute.type !== CONCENTRATION_ATTRIBUTE_SENTINEL,
      'Unexpected annotation errors for concentration layer'
    );
    const errorsByType = groupBy(errors, (e) => e.type);
    if ((errorsByType[PlateMapErrorType.LAYER] ?? []).length > 0) {
      const layerError = errorsByType[PlateMapErrorType.LAYER][0] as LayerPlateMapError;
      assert(
        layerError.layerId === getErrorKeyForDisplayLayerAttribute(selectedAttribute),
        'Only expected to resolve errors for the current layer'
      );
      onRemoveLayer(selectedAttribute);
    } else {
      const updatedPlateMap = clearSchemaFieldErrors<SchemaFieldDisplayLayer>({
        plateMap,
        selectedAttribute,
        assignmentGroupsById: assignmentGroupsById as {[id: string]: SchemaFieldAssignmentGroup},
        wellErrors: (errorsByType[PlateMapErrorType.WELL] ?? []) as Array<WellPlateMapError>,
        fieldValueErrors: (errorsByType[PlateMapErrorType.FIELD_VALUE] ??
          []) as Array<FieldValuePlateMapError>,
      });
      setPlateMap(updatedPlateMap);
    }
  }

  const errorsForLayer =
    groupedValidationErrors != null
      ? (groupedValidationErrors[getErrorKeyForDisplayLayerAttribute(selectedAttribute)] ?? null)
      : null;

  const assignmentGroupsById = useMemo(
    () => assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(selectedAttribute)] ?? {},
    [assignmentGroupsPerAttribute, selectedAttribute]
  );

  const plateMapDisplayParams = useMemo(
    () => ({
      plateMap: plateMapWithPreview ?? plateMap,
      displayLayer,
      attribute: selectedAttribute,
      assignmentGroupsById,
      groupedValidationErrors: groupedValidationErrors ?? {},
    }),
    [
      assignmentGroupsById,
      displayLayer,
      groupedValidationErrors,
      plateMap,
      plateMapWithPreview,
      selectedAttribute,
    ]
  );

  // If we are in AI preview mode, we want to show all wells as previewed. Otherwise, if there is a preview,
  // show only the selected wells as previewed.
  const previewedWellIndices = isAIPreview
    ? new Set(plateMap.wells.map((_, i) => i))
    : plateMapWithPreview != null
      ? selectedWellIndices
      : new Set<number>();

  return (
    <div className={css(styles.wrapper)} {...getTestProps({component: 'AnnotatePlateStep', element: 'root'})}>
      <div className={css(styles.previewSection)} ref={previewSectionRef}>
        <div className={css(styles.errorBanners)}>
          {errorsForLayer && (
            <PlateMapValidationBanners
              errorsByLevel={errorsForLayer}
              onResolveError={onResolveErrors}
              onDismissErrors={onDismissErrors}
            />
          )}
        </div>
        <PlateMapPane
          header={
            <div className={css(styles.previewHeader)} ref={previewHeaderRef}>
              {!isViewOnly ? (
                <PlateMapWellSelectionSection
                  selectedWellIndices={selectedWellIndices}
                  paragraphRef={previewHeaderTextRef}
                  setSelectedWellIndices={onChangeWellSelection}
                  totalWells={plateMap.wells.length}
                />
              ) : (
                // Empty div tag to preserve flex styling on the header
                <div />
              )}
              {isAIPreview && displayLayer === PlateMapWellDisplayLayer.WELL_ROLE ? (
                <Badge color={BadgeColor.BLUE}>AI suggested roles preview</Badge>
              ) : (
                <Button
                  icon="close"
                  size={ButtonSize.SMALL}
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  text={selectedWellIndices.size === 0 ? 'Clear all wells' : 'Clear wells'}
                  onClick={handleClickClear}
                  disabled={isViewOnly}
                />
              )}
            </div>
          }
          plateMapDisplayParams={plateMapDisplayParams}
          isPlateMapViewOnly={isViewOnly ?? false}
          selectedWellIndices={selectedWellIndices}
          setSelectedWellIndices={onChangeWellSelection}
          previewedWellIndices={previewedWellIndices}
          deselectWellsOnClickRefs={[previewSectionRef, previewHeaderRef, previewHeaderTextRef]}
          modalRef={modalRef}
          maxPlateViewHeight={maxPlateViewHeight}
          showFinalMeasurements={showFinalMeasurements}
          plateMapType={plateMapType}
        />
      </div>
      <div className={css(styles.border)} />
      <AnnotatePlateSettings
        plateMap={plateMap}
        onEditPlateMap={onEditPlateMap}
        selectedWellIndices={selectedWellIndices}
        deselectWells={deselectWells}
        setPlateMapWithPreview={setPlateMapWithPreview}
        displayLayer={displayLayer}
        selectedAttribute={selectedAttribute}
        assignmentGroupsById={assignmentGroupsById}
        setAssignmentGroupsById={setAssignmentGroupsForSelectedAttribute}
        setIsAIPreview={setIsAIPreview}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    margin: '-16px',
    padding: '16px',
  },
  errorBanners: {
    marginBottom: '16px',
  },
  previewSection: {
    flex: '1 1 auto',
    overflow: 'auto',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  border: {
    flex: '0 0 0px',
    borderRight: `1px solid ${GRAY_LIGHT}`,
    margin: '-16px 16px',
  },
});
